import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Apple, Loader2, CheckCircle2, Save } from 'lucide-react';
import { api } from './api';

const SLOTS = [
  { id: 'breakfast', label: 'Breakfast' },
  { id: 'lunch',     label: 'Lunch' },
  { id: 'dinner',    label: 'Dinner' },
  { id: 'snack',     label: 'Snack' },
];

const Nutrition = () => {
  const navigate = useNavigate();
  const [slot, setSlot] = useState('breakfast');
  const [foodsRaw, setFoodsRaw] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [insights, setInsights] = useState(null);
  const [error, setError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);

  const refresh = useCallback(async () => {
    try { setInsights((await api.nutritionInsights()).data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const canSubmit = foodsRaw.trim().length >= 2;

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const foods = foodsRaw.split(',').map((s) => s.trim()).filter(Boolean);
      await api.postMeal({ slot, foods });
      setFoodsRaw('');
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/40 via-white to-white pb-24" data-testid="child-nutrition">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate('/childrens-health')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <header className="mb-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Apple className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Nutrition guidance</h1>
              <p className="text-xs text-gray-500 mt-0.5">Log meals — see protein, fruit/veg, sweets patterns.</p>
            </div>
          </div>
        </header>

        <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Log today's meal</p>
          <div className="grid grid-cols-4 gap-1.5 mb-3">
            {SLOTS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSlot(s.id)}
                className={`text-xs sm:text-sm font-medium rounded-lg border-2 px-2 py-2 transition-all ${
                  slot === s.id ? 'border-amber-500 bg-amber-50 text-amber-900' : 'border-gray-200 hover:border-amber-300 text-gray-700'
                }`}
                data-testid={`meal-slot-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={foodsRaw}
            onChange={(e) => setFoodsRaw(e.target.value)}
            placeholder="e.g. eggs, oats, banana"
            className="w-full text-base font-medium border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-amber-400"
            data-testid="meal-foods"
          />
          <p className="text-[11px] text-gray-500 mt-1.5">Separate items with commas. Free-form is fine.</p>
          {error && <p className="text-sm text-rose-700 mt-2">{error}</p>}
          <button
            onClick={submit}
            disabled={!canSubmit || submitting}
            className={`w-full mt-3 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-semibold shadow-sm transition-all ${
              canSubmit && !submitting ? 'bg-gradient-to-br from-amber-500 to-orange-500 hover:brightness-110 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="meal-submit"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Log this meal
          </button>
          {savedFlash && (
            <div className="flex items-center justify-center gap-1.5 mt-2 text-xs text-emerald-700" data-testid="meal-saved">
              <Save className="h-3 w-3" /> Saved
            </div>
          )}
        </div>

        {insights && (
          <>
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4">
              <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-widest mb-2">Today's nutrition focus</p>
              <ul className="space-y-2" data-testid="nutrition-focus">
                {insights.focus?.map((f, i) => (
                  <li key={f} className="text-sm text-gray-800 flex items-start gap-2">
                    <span className="text-amber-600 font-bold">{i + 1}.</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Last 7 days · {insights.meals_logged} meals logged</p>
              <ul className="space-y-2" data-testid="nutrition-observations">
                {insights.observations?.map((o) => (
                  <li key={o} className="text-sm text-gray-700 flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <span>{o}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 grid grid-cols-5 gap-1 text-center text-[10px] text-gray-500" data-testid="nutrition-counts">
                <div><div className="text-base font-bold text-gray-900">{insights.counts?.protein || 0}</div>protein</div>
                <div><div className="text-base font-bold text-gray-900">{insights.counts?.veggies || 0}</div>veggies</div>
                <div><div className="text-base font-bold text-gray-900">{insights.counts?.fruits || 0}</div>fruits</div>
                <div><div className="text-base font-bold text-gray-900">{insights.counts?.grains || 0}</div>grains</div>
                <div><div className="text-base font-bold text-gray-900">{insights.counts?.sweets || 0}</div>sweets</div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Nutrition;
